// CreateUserDialog.tsx
import { useMemo, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Grid, TextField, MenuItem, IconButton, InputAdornment
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { Button } from "Atoms";

type RoleOption = "Machine operator" | "Manager" | "Administrator";

interface Props {
  open: boolean;
  onClose: () => void;
  onSave: (payload: {
    firstName: string;
    lastName: string;
    emailOrUsername: string;
    mobile?: string;
    landline?: string;
    role?: RoleOption;
    /** present only when a username (not an email) was provided */
    password?: string;
  }) => void | Promise<void>;
  loading?: boolean;
  roles?: RoleOption[];
}

const EMAIL_REGEX =
  /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;

const isEmail = (value: string) => EMAIL_REGEX.test(value.trim());

export default function CreateUserDialog({
  open,
  onClose,
  onSave,
  loading = false,
  roles = ["Machine operator", "Manager", "Administrator"],
}: Props) {
  // form state
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName]   = useState("");
  const [emailOrUsername, setEmailOrUsername] = useState("");
  const [mobile, setMobile]       = useState("");
  const [landline, setLandline]   = useState("");
  const [role, setRole]           = useState<RoleOption | "">("");

  // password state (only required when emailOrUsername is NOT an email)
  const isUsernameMode = useMemo(
    () => emailOrUsername.trim() !== "" && !isEmail(emailOrUsername),
    [emailOrUsername]
  );
  const [password, setPassword]             = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPwd, setShowPwd]               = useState(false);
  const [showPwd2, setShowPwd2]             = useState(false);

  const passwordError =
    isUsernameMode && password.length > 0 && password.length < 8
      ? "Password must be at least 8 characters."
      : "";

  const confirmError =
    isUsernameMode && confirmPassword.length > 0 && confirmPassword !== password
      ? "Passwords do not match."
      : "";

  const canSave =
    !!firstName.trim() &&
    !!role &&
    !!lastName.trim() &&
    !!emailOrUsername.trim() &&
    (isUsernameMode
      ? password.length >= 8 && confirmPassword === password
      : true);

  const handleSave = () => {
    if (!canSave) return;

    const payload: any = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      emailOrUsername: emailOrUsername.trim(),
      mobile: mobile.trim() || undefined,
      landline: landline.trim() || undefined,
      role: role || undefined,
    };

    if (isUsernameMode) {
      payload.password = password;
    }

    onSave(payload);
  };

  const resetAndClose = () => {
    setFirstName("");
    setLastName("");
    setEmailOrUsername("");
    setMobile("");
    setLandline("");
    setRole("");
    setPassword("");
    setConfirmPassword("");
    setShowPwd(false);
    setShowPwd2(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : resetAndClose} maxWidth="md" fullWidth>
      <DialogTitle>Create new user</DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={2} sx={{ pt: 0.5 }}>
          <Grid size={6}>
            <TextField
              label="First name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              fullWidth
              required
              autoFocus
            />
          </Grid>

          <Grid size={6}>
            <TextField
              label="Last name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              fullWidth
              required
            />
          </Grid>

          <Grid size={6}>
            <TextField
              label="Email address / Username *"
              value={emailOrUsername}
              onChange={(e) => setEmailOrUsername(e.target.value)}
              fullWidth
              required
              helperText={
                emailOrUsername
                  ? isUsernameMode
                    ? "Looks like a username — password will be required."
                    : "Valid email detected."
                  : "Enter an email address or a username."
              }
            />
          </Grid>

          <Grid size={6}>
            <TextField
              label="Mobile"
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
              fullWidth
            />
          </Grid>

    {/* Conditional password block for usernames */}
          {isUsernameMode && (
            <>
              <Grid size={6}>
                <TextField
                  label="Password"
                  type={showPwd ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  fullWidth
                  required
                  error={!!passwordError}
                  helperText={passwordError || "Minimum 8 characters."}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPwd((v) => !v)}
                          edge="end"
                          aria-label="toggle password visibility"
                        >
                          {showPwd ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid size={6}>
                <TextField
                  label="Confirm password"
                  type={showPwd2 ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  fullWidth
                  required
                  error={!!confirmError}
                  helperText={confirmError || "Re-enter the password."}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPwd2((v) => !v)}
                          edge="end"
                          aria-label="toggle confirm password visibility"
                        >
                          {showPwd2 ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </>
          )}
          <Grid size={6}>
            <TextField
              label="Landline"
              value={landline}
              onChange={(e) => setLandline(e.target.value)}
              fullWidth
            />
          </Grid>

          <Grid size={6}>
            <TextField
              select
              label="Role"
              value={role}
              onChange={(e) => setRole(e.target.value as RoleOption)}
              fullWidth
            >
              {roles.map((r) => (
                <MenuItem key={r} value={r}>
                  {r}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

      
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button variant="outlined" onClick={resetAndClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!canSave || loading}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
